import { useCallback, useEffect, useMemo, useState } from "react"
import { IconLockOutline18 as Lock } from "nucleo-ui-outline-18"

import type {
	FieldRule,
	ResolvedToolPolicyVariant,
	ToolPolicy,
} from "@productlane/lib"
import {
	emptyToolPolicy,
	findShadowedPaths,
	isDescendantPath,
	isGuardingPolicy,
	isTerminalMode,
	MAX_POLICY_VARIANTS_PER_TOOL,
	MAX_REFS_PER_CALL,
	MAX_VARIANT_LABEL_CHARS,
	refsInPolicy,
	resolveToolPolicyVariants,
	variantIdFromLabel,
} from "@productlane/lib"
import { mutators } from "@productlane/zero/mutators"

import type { SchemaField } from "../model/tool-schema-fields"
import { toast } from "@/components/toast"
import { useZero } from "@/lib/zero"
import { childFieldsOf } from "../model/tool-schema-fields"

export type VariantPatch = Partial<
	Pick<ResolvedToolPolicyVariant, "label" | "description" | "unknownFields">
> & { fields?: Record<string, FieldRule> }

/** The base variant lives on the policy itself, so it patches different keys. */
function patchVariant(
	policy: ToolPolicy,
	variantId: string,
	patch: VariantPatch,
): ToolPolicy {
	if (!variantId) return { ...policy, ...patch }
	return {
		...policy,
		variants: (policy.variants ?? []).map((variant) =>
			variant.id === variantId
				? { ...variant, ...patch, label: patch.label ?? variant.label }
				: variant,
		),
	}
}

// The schema refuses a policy it cannot store, and its first issue names the rule
// that broke, which reads better than the whole validation dump.
function saveErrorMessage(error: unknown): string {
	const issues = (error as { issues?: { message?: string }[] } | null)?.issues
	const first = issues?.[0]?.message
	if (first) return first
	return error instanceof Error ? error.message : "Try again in a moment."
}

interface Options {
	open: boolean
	sectionId: string
	toolName: string
	policy: ToolPolicy | null
	schemaFields: readonly SchemaField[]
}

/**
 * The policy the sheet is editing. Every write saves, so nothing here waits for a
 * submit; the schema is what refuses a policy that cannot be stored.
 */
export function usePolicyDraft({
	open,
	sectionId,
	toolName,
	policy,
	schemaFields,
}: Options) {
	const zero = useZero()
	const [draft, setDraft] = useState<ToolPolicy>(policy ?? emptyToolPolicy())
	const [activeId, setActiveId] = useState("")

	useEffect(() => {
		if (open) {
			setDraft(policy ?? emptyToolPolicy())
			setActiveId("")
		}
	}, [open, policy])

	const variants = useMemo(
		() => resolveToolPolicyVariants(draft, toolName),
		[draft, toolName],
	)
	const active = variants.find((entry) => entry.id === activeId) ?? variants[0]!

	const persist = useCallback(
		(next: ToolPolicy) => {
			try {
				zero.mutate(
					mutators.sidebarSections.setToolPolicy({
						id: sectionId,
						tool: toolName,
						policy: isGuardingPolicy(next) ? next : null,
					}),
				)
			} catch (error) {
				toast.error({
					title: "Could not save this field policy",
					description: saveErrorMessage(error),
					icon: Lock,
				})
			}
		},
		[sectionId, toolName, zero],
	)

	const updateActive = useCallback(
		(patch: VariantPatch, options: { save?: boolean } = {}) => {
			setDraft((current) => {
				const next = patchVariant(current, activeId, patch)
				if (options.save !== false) persist(next)
				return next
			})
		},
		[activeId, persist],
	)

	const writeRule = useCallback(
		(
			path: string,
			rule: FieldRule | null,
			options: { save?: boolean } = {},
		) => {
			const fields = { ...active.fields }
			if (!rule || rule.mode === "any") delete fields[path]
			else fields[path] = rule
			updateActive({ fields }, options)
		},
		[active.fields, updateActive],
	)

	const addVariant = useCallback(
		(copyFrom?: ResolvedToolPolicyVariant) => {
			const existing = draft.variants ?? []
			if (existing.length + 1 >= MAX_POLICY_VARIANTS_PER_TOOL) {
				toast.info({
					title: `A tool can have at most ${MAX_POLICY_VARIANTS_PER_TOOL} configurations`,
					description: "Delete one you no longer need before adding another.",
					icon: Lock,
				})
				return
			}

			const label = copyFrom
				? `${copyFrom.label} copy`.slice(0, MAX_VARIANT_LABEL_CHARS)
				: `Configuration ${existing.length + 2}`
			const id = variantIdFromLabel(label, [
				"",
				...existing.map((variant) => variant.id),
			])
			const next: ToolPolicy = {
				...draft,
				variants: [
					...existing,
					{
						id,
						label,
						...(copyFrom?.description && { description: copyFrom.description }),
						unknownFields: copyFrom?.unknownFields ?? "allow",
						fields: { ...(copyFrom?.fields ?? {}) },
					},
				],
			}
			setDraft(next)
			persist(next)
			setActiveId(id)
		},
		[draft, persist],
	)

	const removeVariant = useCallback(
		(id: string) => {
			const next: ToolPolicy = {
				...draft,
				variants: (draft.variants ?? []).filter((variant) => variant.id !== id),
			}
			setDraft(next)
			persist(next)
			setActiveId("")
		},
		[draft, persist],
	)

	// A hand-typed path is kept alongside the schema rows so a policy still works
	// against a tool whose schema we could not read.
	const extraPaths = useMemo(() => {
		const known = new Set(schemaFields.map((field) => field.path))
		return Object.keys(active.fields).filter((path) => !known.has(path))
	}, [active.fields, schemaFields])

	// A pinned or blocked container owns its subtree, so its children are shown as
	// covered rather than as rules that silently never run.
	const shadowedPaths = useMemo(() => {
		const map = new Map<string, string>()
		for (const conflict of findShadowedPaths(active.fields, (rule) =>
			isTerminalMode(rule.mode),
		)) {
			map.set(conflict.path, conflict.shadowedBy)
		}
		for (const field of schemaFields) {
			if (map.has(field.path)) continue
			const owner = Object.entries(active.fields).find(
				([path, rule]) =>
					isTerminalMode(rule.mode) && isDescendantPath(field.path, path),
			)
			if (owner) map.set(field.path, owner[0])
		}
		return map
	}, [active.fields, schemaFields])

	// Stable per group, so the JSON editor's memo does not churn every render.
	const knownKeysByPath = useMemo(
		() =>
			new Map(
				schemaFields
					.filter((field) => field.kind === "group")
					.map((field) => [
						field.path,
						childFieldsOf(schemaFields, field).map((child) => child.name),
					]),
			),
		[schemaFields],
	)

	return {
		variants,
		active,
		activeId,
		setActiveId,
		updateActive,
		writeRule,
		addVariant,
		removeVariant,
		extraPaths,
		shadowedPaths,
		knownKeysByPath,
		guardedCount: Object.keys(active.fields).length,
		// One call resolves one configuration, so the budget is per configuration.
		refBudgetLeft: MAX_REFS_PER_CALL - refsInPolicy(active).length,
	}
}
